/**
 * Portal Manta · OrcamentoConsolidadoPage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { DADOS } from '../../data/lotes-pr.js';
import { V, S, fmtNum, sumBy, fmtBRL, isAmpliacao } from '../../globals.js';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

const OrcamentoConsolidadoPage = memo(function OrcamentoConsolidadoPage_Inner(){
  const lotes = ['3','4','5'];

  const consolidado = useMemo(()=>{
    // Para cada item 1.2.x, soma os 3 lotes
    const codigos = ['1.2.1','1.2.2','1.2.3','1.2.4','1.2.5','1.2.6','1.2.7','1.2.8','1.2.9','1.2.10','1.2.11','1.2.12','1.2.13'];
    const linhas = codigos.map(cod => {
      let totalEdital = 0;
      const porLote = {};
      lotes.forEach(sig => {
        const dm = (DADOS[sig]?.dados_manta||{}).compara_parametrico||{};
        const itens = (dm.itens||[]).filter(isAmpliacao);
        const it = itens.find(x => x.codigo === cod);
        const v = it?.pr_vias?.preco_total || 0;
        porLote[sig] = v;
        totalEdital += v;
      });
      const desc = (lotes.map(sig => {
        const dm = (DADOS[sig]?.dados_manta||{}).compara_parametrico||{};
        const itens = (dm.itens||[]).filter(isAmpliacao);
        return itens.find(x => x.codigo === cod)?.descricao;
      }).find(x=>x)) || cod;
      return {cod, desc, porLote, total: totalEdital};
    }).filter(l => l.total > 0);

    // Total Geral
    const totalGeral = sumBy(linhas, 'total');
    // Por lote
    const totalPorLote = {};
    lotes.forEach(sig => {
      const cf = CONSOL_FIN[sig] || {};
      totalPorLote[sig] = Object.values(cf).reduce((s,a)=>s+sumBy(a,'v'),0);
    });

    // Extensões e CAPEX
    const ext = {};
    const capex = {};
    lotes.forEach(sig => {
      ext[sig] = parseFloat((DADOS[sig]?.extensao||'').replace(/[^\d,.]/g,'').replace(',','.')) || 0;
      capex[sig] = parseFloat((DADOS[sig]?.capex||'').replace(/[^\d,.]/g,'').replace(',','.')) || 0;
    });
    const extTotal = Object.values(ext).reduce((s,v)=>s+v,0);

    return {linhas, totalGeral, totalPorLote, ext, capex, extTotal};
  }, []);

  const corLote = (sig) => ({'3':V.gn, '4':V.bl, '5':V.or})[sig] || V.gr;

  return(<div style={S.page}>
    <div style={S.h1}>📊 Orçamentação Consolidada — 3 lotes ANTT-PR</div>
    <div style={S.sub}>Soma das obras 1.2.x (Ampliação) dos lotes L3, L4 e L5 · referência rápida pra benchmarks</div>

    {/* KPIs gerais consolidados */}
    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:14}}>
      <div style={{...S.cardP,padding:14}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3,marginBottom:4,textTransform:'uppercase'}}>Lotes</div>
        <div style={{fontSize:24,fontWeight:800,color:V.br}}>3</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>L3 + L4 + L5</div>
      </div>
      <div style={{...S.cardP,padding:14}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3,marginBottom:4,textTransform:'uppercase'}}>Extensão total</div>
        <div style={{fontSize:24,fontWeight:800,color:V.br,fontFamily:'monospace'}}>{fmtNum(consolidado.extTotal,1)}</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>km</div>
      </div>
      <div style={{...S.cardP,padding:14}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3,marginBottom:4,textTransform:'uppercase'}}>Total Obras 1.2.x</div>
        <div style={{fontSize:24,fontWeight:800,color:V.or,fontFamily:'monospace'}}>{fmtBRL(consolidado.totalGeral,0)}</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>edital PR Vias</div>
      </div>
      <div style={{...S.cardP,padding:14}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3,marginBottom:4,textTransform:'uppercase'}}>R$/km médio</div>
        <div style={{fontSize:24,fontWeight:800,color:V.bl,fontFamily:'monospace'}}>{fmtBRL(consolidado.extTotal>0 ? consolidado.totalGeral/consolidado.extTotal : 0,0)}</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>obras 1.2.x / km</div>
      </div>
    </div>

    {/* Tabela consolidada */}
    <div style={{...S.cardP,padding:0,overflow:'hidden',marginBottom:14}}>
      <div style={{padding:'10px 14px',background:'linear-gradient(90deg,'+V.br+','+V.br+'cc)',color:'#fff',fontSize:11,fontWeight:800,letterSpacing:.05,textTransform:'uppercase'}}>
        Soma por código de obra · 1.2.x (Ampliação)
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={{...THl,width:70}}>Código</th>
          <th style={THl}>Descrição</th>
          <th style={{...TH,width:130,background:'#f0fdf4'}}>Lote 3</th>
          <th style={{...TH,width:130,background:'#eff6ff'}}>Lote 4</th>
          <th style={{...TH,width:130,background:'#fff7ed'}}>Lote 5</th>
          <th style={{...TH,width:140,background:V.g0}}>Total Consolidado</th>
        </tr></thead>
        <tbody>
          {consolidado.linhas.map(l => (
            <tr key={l.cod} style={S.trLine}>
              <td style={{...TDl,fontFamily:'monospace',fontWeight:700,color:V.bl}}>{l.cod}</td>
              <td style={TDl}>{l.desc}</td>
              <td style={{...TD,fontFamily:'monospace',background:'#f0fdf4',color:V.gn}}>{l.porLote['3']>0?fmtBRL(l.porLote['3'],0):'—'}</td>
              <td style={{...TD,fontFamily:'monospace',background:'#eff6ff',color:V.bl}}>{l.porLote['4']>0?fmtBRL(l.porLote['4'],0):'—'}</td>
              <td style={{...TD,fontFamily:'monospace',background:'#fff7ed',color:V.or}}>{l.porLote['5']>0?fmtBRL(l.porLote['5'],0):'—'}</td>
              <td style={{...TD,fontFamily:'monospace',fontWeight:700,background:V.g0,color:V.br}}>{fmtBRL(l.total,0)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'3px solid '+V.br,background:'#FDF0E8'}}>
            <td colSpan={2} style={{...TDl,fontWeight:800,color:V.br,fontSize:11.5,padding:'10px 12px'}}>TOTAL GERAL</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,color:V.gn,fontSize:11.5,padding:'10px 12px'}}>{fmtBRL(lotes.map(s=>sumBy(consolidado.linhas,l=>l.porLote[s]||0))[0],0)}</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,color:V.bl,fontSize:11.5,padding:'10px 12px'}}>{fmtBRL(lotes.map(s=>sumBy(consolidado.linhas,l=>l.porLote[s]||0))[1],0)}</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,color:V.or,fontSize:11.5,padding:'10px 12px'}}>{fmtBRL(lotes.map(s=>sumBy(consolidado.linhas,l=>l.porLote[s]||0))[2],0)}</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,fontSize:13,background:V.or,padding:'10px 12px',color:'#fff'}}>{fmtBRL(consolidado.totalGeral,0)}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    {/* Comparativo SICRO total por lote */}
    <div style={{...S.cardP,padding:14}}>
      <div style={{fontSize:11,fontWeight:800,color:V.br,textTransform:'uppercase',letterSpacing:.05,marginBottom:10}}>
        Total SICRO consolidado por lote
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={THl}>Lote</th>
          <th style={{...TH,width:100}}>Extensão (km)</th>
          <th style={{...TH,width:140}}>CAPEX edital (R$ bi)</th>
          <th style={{...TH,width:140}}>Σ SICRO (R$)</th>
          <th style={{...TH,width:120}}>R$/km SICRO</th>
        </tr></thead>
        <tbody>
          {lotes.map(sig => (
            <tr key={sig} style={S.trLine}>
              <td style={TDl}>
                <span style={{display:'inline-block',width:10,height:10,borderRadius:99,background:corLote(sig),marginRight:8}}/>
                <b style={{color:corLote(sig)}}>{DADOS[sig]?.sigla || 'L'+sig}</b> · {DADOS[sig]?.nome?.replace(/^Paraná · /,'')}
              </td>
              <td style={{...TD,fontFamily:'monospace'}}>{fmtNum(consolidado.ext[sig],1)}</td>
              <td style={{...TD,fontFamily:'monospace'}}>R$ {fmtNum(consolidado.capex[sig],2)} bi</td>
              <td style={{...TD,fontFamily:'monospace',fontWeight:700,color:corLote(sig)}}>{fmtBRL(consolidado.totalPorLote[sig],0)}</td>
              <td style={{...TD,fontFamily:'monospace',color:V.gr}}>{consolidado.ext[sig]>0?fmtBRL(consolidado.totalPorLote[sig]/consolidado.ext[sig],0):'—'}</td>
            </tr>
          ))}
          <tr style={{borderTop:'3px solid '+V.br,background:'#FDF0E8'}}>
            <td style={{...TDl,fontWeight:800,color:V.br,fontSize:11.5,padding:'10px 12px'}}>TOTAL CONSOLIDADO</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,padding:'10px 12px'}}>{fmtNum(consolidado.extTotal,1)}</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,padding:'10px 12px'}}>R$ {fmtNum(Object.values(consolidado.capex).reduce((s,v)=>s+v,0),2)} bi</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,color:V.br,padding:'10px 12px'}}>{fmtBRL(Object.values(consolidado.totalPorLote).reduce((s,v)=>s+v,0),0)}</td>
            <td style={{...TD,fontFamily:'monospace',fontWeight:800,color:V.br,padding:'10px 12px'}}>{fmtBRL(consolidado.extTotal>0?Object.values(consolidado.totalPorLote).reduce((s,v)=>s+v,0)/consolidado.extTotal:0,0)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>);
});

export { OrcamentoConsolidadoPage };
export default OrcamentoConsolidadoPage;
